import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Lock, User, Phone, ArrowRight, MapPin, Eye, EyeOff, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register } = useAuth();
  
  const [form, setForm] = useState({
    email: "", password: "", nom: "", prenom: "", telephone: "",
    adresse: "", code_postal: "", ville: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const [resendingVerification, setResendingVerification] = useState(false);
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);

  const handleResendVerification = async () => {
    setResendingVerification(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: form.email });
      toast.success("Email de vérification renvoyé !");
    } catch {
      toast.error("Erreur lors de l'envoi");
    } finally {
      setResendingVerification(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setShowVerificationMessage(false);
    
    try {
      if (isLogin) {
        await login(form.email, form.password);
        toast.success("Connexion réussie !");
      } else {
        await register(form);
        // Show verification message instead of navigating
        setShowVerificationMessage(true);
        setLoading(false);
        return;
      }
      
      const from = location.state?.from || "/mon-espace";
      // Si admin, rediriger vers admin
      if (isLogin) {
        const res = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("iks_token")}` }
        });
        if (res.data?.role === "admin") {
          navigate(location.state?.from || "/admin");
          return;
        }
      }
      navigate(from);
    } catch (error) {
      const status = error.response?.status;
      const msg = error.response?.data?.detail || "Une erreur est survenue";
      if (status === 403 && msg.includes("vérifier")) {
        setShowVerificationMessage(true);
        toast.error("Veuillez d'abord vérifier votre email");
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    "50% de crédit d'impôt",
    "Réservation en ligne 24h/24",
    "Suivi de vos prestations",
    "Programme parrainage : 15€ offerts"
  ];

  return (
    <div className="pt-16 min-h-screen bg-gray-50 flex flex-col lg:flex-row" data-testid="auth-page">
      {/* Left - Branding (desktop only) */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#003264] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
        </div>
        
        <div className="relative z-10 flex flex-col justify-center px-16 py-12 w-full">
          <Link to="/">
            <img src="/logo-iks.png" alt="IKS" className="h-20 w-auto object-contain mb-12" />
          </Link>
          
          <h2 className="text-3xl font-bold text-white mb-4">
            Bienvenue chez IKS
          </h2>
          <p className="text-white/70 text-lg mb-10 max-w-md">
            Votre partenaire de confiance pour un intérieur impeccable
          </p>
          
          <div className="space-y-4">
            {benefits.map((benefit, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                </div>
                <span className="text-white/80">{benefit}</span>
              </div>
            ))}
          </div>
          
          <div className="mt-auto pt-16">
            <blockquote className="border-l-2 border-white/30 pl-6">
              <p className="text-white/60 italic">
                "Service impeccable, équipe ponctuelle et professionnelle. Je recommande !"
              </p>
              <footer className="mt-2 text-white/40 text-sm">— Marie D., Montpellier</footer>
            </blockquote>
          </div>
        </div>
      </div>

      {/* Right - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center py-8 px-4 sm:px-6 sm:py-12">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-6">
            <Link to="/">
              <img src="/logo-iks.png" alt="IKS Propreté & Services" className="h-16 w-auto mx-auto mb-2" data-testid="mobile-auth-logo" />
            </Link>
          </div>

          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-[#003264] mb-2">
              {isLogin ? "Connexion" : "Créer un compte"}
            </h1>
            <p className="text-gray-500">
              {isLogin 
                ? "Accédez à votre espace personnel" 
                : "Inscrivez-vous pour réserver vos prestations"}
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-xl shadow-gray-100/50">
            {showVerificationMessage ? (
              <div className="text-center py-4" data-testid="verification-message">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-[#003264]" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Verifiez votre email</h3>
                <p className="text-gray-500 text-sm mb-6">
                  Un email de verification a ete envoye a <strong>{form.email}</strong>. Cliquez sur le lien pour activer votre compte.
                </p>
                <button
                  onClick={handleResendVerification}
                  disabled={resendingVerification}
                  className="text-sm text-[#003264] hover:underline font-medium disabled:opacity-50"
                  data-testid="resend-verification-btn"
                >
                  {resendingVerification ? "Envoi en cours..." : "Renvoyer l'email de verification"}
                </button>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <button onClick={() => { setShowVerificationMessage(false); setIsLogin(true); }} className="text-sm text-gray-500 hover:text-gray-700">
                    Retour a la connexion
                  </button>
                </div>
              </div>
            ) : (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="auth-form">
              {!isLogin && (
                <>
                  {/* Name fields */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-700 text-sm font-medium">Nom</Label>
                      <div className="relative mt-1.5">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input 
                          name="nom" 
                          value={form.nom} 
                          onChange={handleChange} 
                          required 
                          className="pl-10 h-12 border-gray-200 focus:border-[#003264]"
                          placeholder="Dupont"
                          data-testid="input-nom"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-gray-700 text-sm font-medium">Prénom</Label>
                      <div className="relative mt-1.5">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input 
                          name="prenom" 
                          value={form.prenom} 
                          onChange={handleChange} 
                          required 
                          className="pl-10 h-12 border-gray-200 focus:border-[#003264]"
                          placeholder="Jean"
                          data-testid="input-prenom"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Phone */}
                  <div>
                    <Label className="text-gray-700 text-sm font-medium">Téléphone</Label>
                    <div className="relative mt-1.5">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input 
                        name="telephone" 
                        value={form.telephone} 
                        onChange={handleChange} 
                        required 
                        className="pl-10 h-12 border-gray-200 focus:border-[#003264]"
                        placeholder="06 12 34 56 78"
                        data-testid="input-telephone"
                      />
                    </div>
                  </div>
                  
                  {/* Address section */}
                  <div className="pt-4 border-t border-gray-100">
                    <Label className="text-[#003264] text-sm font-semibold flex items-center gap-2 mb-4">
                      <MapPin className="w-4 h-4" />
                      Adresse d'intervention
                    </Label>
                    <div className="space-y-4">
                      <Input 
                        name="adresse" 
                        value={form.adresse} 
                        onChange={handleChange} 
                        required 
                        className="h-12 border-gray-200 focus:border-[#003264]"
                        placeholder="Numéro et rue"
                        data-testid="input-adresse"
                      />
                      <div className="grid grid-cols-2 gap-4">
                        <Input 
                          name="code_postal" 
                          value={form.code_postal} 
                          onChange={handleChange} 
                          required 
                          className="h-12 border-gray-200 focus:border-[#003264]"
                          placeholder="Code postal"
                          maxLength={5}
                          data-testid="input-code-postal"
                        />
                        <Input 
                          name="ville" 
                          value={form.ville} 
                          onChange={handleChange} 
                          required 
                          className="h-12 border-gray-200 focus:border-[#003264]"
                          placeholder="Ville"
                          data-testid="input-ville"
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Email */}
              <div>
                <Label className="text-gray-700 text-sm font-medium">Email</Label>
                <div className="relative mt-1.5">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input 
                    name="email" 
                    type="email"
                    value={form.email} 
                    onChange={handleChange} 
                    required 
                    className="pl-10 h-12 border-gray-200 focus:border-[#003264]"
                    placeholder="jean.dupont@email.com"
                    data-testid="input-email"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <Label className="text-gray-700 text-sm font-medium">Mot de passe</Label>
                <div className="relative mt-1.5">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input 
                    name="password" 
                    type={showPassword ? "text" : "password"}
                    value={form.password} 
                    onChange={handleChange} 
                    required 
                    minLength={6}
                    className="pl-10 pr-12 h-12 border-gray-200 focus:border-[#003264]"
                    placeholder="••••••••"
                    data-testid="input-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {!isLogin && (
                  <p className="text-xs text-gray-500 mt-1.5">Minimum 6 caractères</p>
                )}
              </div>

              <Button 
                type="submit" 
                disabled={loading}
                className="w-full bg-[#003264] hover:bg-[#002244] text-white h-12 text-base btn-shine"
                data-testid="btn-submit"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                    Chargement...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    {isLogin ? "Se connecter" : "Créer mon compte"}
                    <ArrowRight className="w-4 h-4" />
                  </span>
                )}
              </Button>
            </form>
            )}

            {!showVerificationMessage && (
            <div className="mt-6 text-center">
              <button
                onClick={() => { setIsLogin(!isLogin); setShowVerificationMessage(false); }}
                className="text-sm text-gray-600 hover:text-[#003264] transition-colors"
              >
                {isLogin 
                  ? <>Pas encore de compte ? <span className="font-semibold text-[#003264]">Créer un compte</span></> 
                  : <>Déjà un compte ? <span className="font-semibold text-[#003264]">Se connecter</span></>}
              </button>
            </div>
            )}
          </div>

          {/* Trust badges */}
          <div className="mt-8 flex items-center justify-center gap-6 text-xs text-gray-400">
            <span className="flex items-center gap-1">
              <Lock className="w-3 h-3" />
              Connexion sécurisée
            </span>
            <span>•</span>
            <span>Données protégées</span>
          </div>
        </div>
      </div>
    </div>
  );
};
